import { createContext } from 'preact';
import { useContext, useState, useEffect, useCallback, useRef } from 'preact/hooks';
import type { ComponentChildren } from 'preact';
import type { CheckoutState, AlohaPayConfig, Country, CustomerData } from '../types';
import { getPaymentMethods, getWallets, createPaymentRequest, buildCheckoutUrl, getPaymentLinkStatus, extractPaymentLinkId } from '../services/api';

interface CheckoutContextValue extends CheckoutState {
  setSelectedCountry: (country: Country) => void;
  setCustomerData: (data: CustomerData) => void;
  nextStep: () => void;
  prevStep: () => void;
  submitCheckout: () => Promise<void>;
}

const CheckoutContext = createContext<CheckoutContextValue | null>(null);

interface CheckoutProviderProps {
  config: AlohaPayConfig;
  children: ComponentChildren;
}

export function CheckoutProvider({ config, children }: CheckoutProviderProps) {
  const [state, setState] = useState<CheckoutState>({
    step: 'form',
    selectedCountry: null,
    customerData: config.customer || {},
    countries: [],
    walletCurrency: null,
    loading: true,
    error: null,
    config,
    checkoutUrl: null,
    paymentLinkId: null
  });

  const pollingRef = useRef<number | null>(null);
  const POLLING_INTERVAL = 3000; // 3 seconds

  // Polling para verificar el estado del pago
  useEffect(() => {
    const shouldPoll = state.step === 'waiting' && state.paymentLinkId && state.config;

    if (!shouldPoll) {
      // Limpiar polling si no estamos en estado waiting
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      return;
    }

    const checkPaymentStatus = async () => {
      try {
        const response = await getPaymentLinkStatus(state.config!.apiKey, state.paymentLinkId!, state.config!.useSandbox);

        if (response.success && response.data.status === 'completed') {
          // Detener polling
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }

          // Cambiar al estado completado
          setState(prev => ({ ...prev, step: 'completed' }));

          // Llamar callback
          state.config?.onPaymentComplete?.();
        }
      } catch (err) {
        // Silenciar errores de polling para no interrumpir la experiencia
        console.warn('Payment status check failed:', err);
      }
    };

    // Hacer una verificación inicial
    checkPaymentStatus();

    // Iniciar polling
    pollingRef.current = window.setInterval(checkPaymentStatus, POLLING_INTERVAL);

    // Cleanup
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [state.step, state.paymentLinkId, state.config]);

  // Cargar wallets y métodos de pago al iniciar
  useEffect(() => {
    async function loadInitialData() {
      try {
        // Cargar ambos en paralelo
        const [walletsResponse, paymentMethodsResponse] = await Promise.all([
          getWallets(config.apiKey, config.useSandbox),
          getPaymentMethods(config.apiKey, config.useSandbox)
        ]);

        if (!walletsResponse.success) {
          throw new Error(walletsResponse.message);
        }

        if (!paymentMethodsResponse.success) {
          throw new Error(paymentMethodsResponse.message);
        }

        const walletCurrency = walletsResponse.data[0]?.currency || null;
        const countries = paymentMethodsResponse.data;
        let selectedCountry: Country | null = null;

        // Pre-seleccionar país si viene defaultCountry
        if (config.defaultCountry) {
          selectedCountry = countries.find(c => c.country_code === config.defaultCountry) || null;
        }

        setState(prev => ({
          ...prev,
          countries,
          selectedCountry,
          walletCurrency,
          loading: false
        }));

        config.onReady?.();
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to load data');
        setState(prev => ({ ...prev, error: error.message, loading: false }));
        config.onError?.(error);
      }
    }

    loadInitialData();
  }, [config]);

  const setSelectedCountry = useCallback((country: Country) => {
    setState(prev => ({ ...prev, selectedCountry: country }));
  }, []);

  const setCustomerData = useCallback((data: CustomerData) => {
    setState(prev => ({ ...prev, customerData: { ...prev.customerData, ...data } }));
  }, []);

  const nextStep = useCallback(() => {
    setState(prev => ({ ...prev, step: 'waiting' }));
  }, []);

  const prevStep = useCallback(() => {
    setState(prev => ({ ...prev, step: 'form' }));
  }, []);

  const submitCheckout = useCallback(async () => {
    if (!state.selectedCountry || !state.config) return;

    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const response = await createPaymentRequest({
        apiKey: state.config.apiKey,
        amount: state.config.amount,
        clientCurrency: state.selectedCountry.currency,
        concept: state.config.description,
        customer: state.customerData
      }, state.config.useSandbox);

      if (response.success) {
        const checkoutUrl = buildCheckoutUrl(response.checkoutUrl, state.customerData);
        const paymentLinkId = extractPaymentLinkId(response.checkoutUrl);

        // Llamar callback cuando el link se crea
        state.config.onPaymentLinkCreated?.(checkoutUrl);

        // Abrir checkout en nueva pestaña
        window.open(checkoutUrl, '_blank');

        // Cambiar al estado de espera
        setState(prev => ({
          ...prev,
          step: 'waiting',
          loading: false,
          checkoutUrl,
          paymentLinkId
        }));
      } else {
        throw new Error('Failed to create payment');
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to create payment');
      setState(prev => ({ ...prev, error: error.message, loading: false }));
      state.config?.onError?.(error);
    }
  }, [state.selectedCountry, state.config, state.customerData]);

  const value: CheckoutContextValue = {
    ...state,
    setSelectedCountry,
    setCustomerData,
    nextStep,
    prevStep,
    submitCheckout
  };

  return (
    <CheckoutContext.Provider value={value}>
      {children}
    </CheckoutContext.Provider>
  );
}

export function useCheckout(): CheckoutContextValue {
  const context = useContext(CheckoutContext);
  if (!context) {
    throw new Error('useCheckout must be used within CheckoutProvider');
  }
  return context;
}
